import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { googleOAuth, isApiConfigured } from "../auth-config";
import { postOAuthComplete } from "../auth/api-client";
import {
  clearPkceBundle,
  loadPkceBundle,
  saveProfile,
  loadProfile,
  saveGoogleTokens,
  saveAppJwt,
} from "../auth/auth-session";
import type { UserRole } from "../auth/auth-types";
import { resolvePostAuthRoute, buildLoginPath, loadPostLoginReturnTo } from "../auth/post-login-redirect";
import { useAuth } from "../auth/AuthContext";
import SupportContactHint from "./legal/SupportContactHint";

function persistProfileFromSessionUser(
  user: { googleSub: string; email: string; role: string; enrollmentSummary?: string },
  prev: ReturnType<typeof loadProfile>
) {
  const role: UserRole =
    user.role === "teacher" || user.role === "student" || user.role === "pending"
      ? user.role
      : "pending";
  const enrollmentSummary =
    user.enrollmentSummary === "active" ||
    user.enrollmentSummary === "none" ||
    user.enrollmentSummary === "archived_only"
      ? user.enrollmentSummary
      : undefined;
  saveProfile({
    googleSub: user.googleSub,
    email: user.email,
    role,
    displayName: prev?.googleSub === user.googleSub ? prev.displayName : undefined,
    enrollmentSummary,
  });
}

export default function GoogleCallbackPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [message, setMessage] = useState("Hang tight, we're signing you in with Google…");
  const [phase, setPhase] = useState<"working" | "error">("working");

  useEffect(() => {
    const fail = (text: string) => {
      setMessage(text);
      setPhase("error");
    };
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");
    const oauthDesc = params.get("error_description");

    if (oauthError) {
      const canceled =
        oauthError === "access_denied" ||
        oauthDesc?.toLowerCase().includes("cancel");
      fail(
        canceled
          ? "You closed the Google sign-in window. Try again when you're ready."
          : "Google sign-in didn't work. Go back and try again, or ask your teacher for help."
      );
      return;
    }

    if (!code || !state) {
      fail("Something went wrong. Go back to sign in and try again.");
      return;
    }

    if (!isApiConfigured()) {
      fail(
        import.meta.env.DEV
          ? "This site build is missing the HTTP API URL (VITE_API_BASE_URL). Google Web sign-in must exchange the authorization code on the server. For production: ensure GitHub Actions can resolve CodeCreateSlay-Prod-ApiStack output PublicApiUrl, or set repository Variable or Secret CCS_API_URL to that URL and redeploy. For local dev: set VITE_API_BASE_URL in apps/web/.env.local."
          : "Sign-in isn't working on this site yet. Ask your teacher for help."
      );
      return;
    }

    const bundle = loadPkceBundle();
    if (!bundle || bundle.state !== state) {
      fail("That took too long. Go back to sign in and try again.");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const prevProfile = loadProfile();

        const session = await postOAuthComplete({
          code,
          codeVerifier: bundle.codeVerifier,
          redirectUri: googleOAuth.redirectUri,
        });
        if (cancelled) return;
        const idToken = session.googleIdToken;
        if (!idToken) throw new Error("Google did not return an ID token.");

        clearPkceBundle();
        saveGoogleTokens(idToken, session.googleAccessToken);
        saveAppJwt(session.accessToken);
        persistProfileFromSessionUser(session.user, prevProfile);

        refresh();

        const profile = loadProfile();
        if (!profile) throw new Error("Profile could not be loaded after sign-in.");
        navigate(resolvePostAuthRoute(profile), { replace: true });
      } catch (err: unknown) {
        if (cancelled) return;
        clearPkceBundle();
        const text = err instanceof Error ? err.message : "Unexpected error";
        fail(text);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, refresh]);

  return (
    <main className="page">
      <div className="card" aria-busy={phase === "working"}>
        <p className="eyebrow">Signing in</p>
        <h1>Almost there</h1>
        {phase === "working" ? (
          <div className="auth-callback-loading" role="status" aria-live="polite">
            <span className="auth-callback-spinner" aria-hidden="true" />
            <p className="lede auth-callback-loading-text">{message}</p>
          </div>
        ) : (
          <>
            <p className="lede" role="alert">
              {message}
            </p>
            <div className="actions">
              <button
                type="button"
                className="btn secondary"
                onClick={() => navigate(buildLoginPath(loadPostLoginReturnTo()), { replace: true })}
              >
                Back to login
              </button>
            </div>
          </>
        )}
        <SupportContactHint className="support-contact-hint fineprint" />
      </div>
    </main>
  );
}
